import type { ReactNode } from "react";
import { useDeferredValue, useEffect, useMemo, useState } from "react";
import type { Project } from "../../../leaderboard/types/index.ts";
import {
  useAppendMessageLogMutation,
  useAttachLinkedInIdentityMutation,
  useCaptureTelegramRelationshipMutation,
  useCaptureTwitterRelationshipMutation,
  useDiscardXQueueDraftMutation,
  useDisconnectXMutation,
  useEvContactsQuery,
  useGenerateXPromptExamplesMutation,
  useRecordTouchpointsMutation,
  useRepairXQueueDraftMutation,
  useSaveXPromptSettingsMutation,
  useStartXConnectMutation,
  useTelegramAuthMutations,
  useTelegramSendMutation,
  useTelegramStatusQuery,
  useUpdateOutreachRelationshipMutation,
  useUpdateOutreachRelationshipTargetMutation,
  useUpdateRelationshipFollowUpMutation,
  useXAuditTrailQuery,
  useXConnectionStatusQuery,
  useXPreviewMessageMutation,
  useXPromptSettingsQuery,
  useXQueueContactsQuery,
  useXQueueDraftsQuery,
  useXQueuePreviewMutation,
  useXQueueSendMutation,
  useXResolveHandleMutation,
  useXSendMessageMutation,
  useXThreadQuery,
} from "../../hooks/use-ev-workstation-queries.ts";
import { isBareAmbiguousHandle, parseContactUrl } from "../../lib/contact-url-parser.ts";
import type { WorkstationLane } from "../../lib/workstation-lanes.ts";
import type {
  EvContactRelationshipSummary,
  EvContactSearchResult,
  EvIntroContact,
  EvIntroContactUpdateInput,
  EvMessageLogRequest,
  EvTouchpointRequest,
  EvWorkflowEventPayload,
  EvWorkflowPatch,
  EvWorkflowStage,
  EvWorkstationProject,
  EvXResolveHandlePayload,
} from "../../types/index.ts";

interface EvWorkstationDashboardProps {
  lane: WorkstationLane;
  onLaneChange: (lane: WorkstationLane) => void;
  queue: EvWorkstationProject[];
  leadCount: number;
  inTouchCount: number;
  activeItem: EvWorkstationProject | null;
  savingProjectIds: Set<string>;
  projectSearch: string;
  onProjectSearchChange: (value: string) => void;
  contactSearch: string;
  onContactSearchChange: (value: string) => void;
  onSelectProject: (projectId: string) => void;
  onNext: () => void;
  onOpenProjectDetail: (
    project: Project,
    options?: { workflowMode?: boolean; detailView?: "full" | "call_prep" | "operating_summary" }
  ) => void;
  onUpdateWorkflow: (projectId: string, patch: EvWorkflowPatch) => Promise<unknown> | undefined;
  onUpdateIntroContact: (
    projectId: string,
    input: EvIntroContactUpdateInput
  ) => Promise<unknown> | undefined;
  onCreateWorkflowEvent: (
    projectId: string,
    event: EvWorkflowEventPayload
  ) => Promise<unknown> | undefined;
  todayBriefNode: ReactNode;
  googleStatusNode: ReactNode;
  addToast: (type: "success" | "error" | "info", title: string, message: string) => void;
}

const STAGES: EvWorkflowStage[] = [
  "lead",
  "group_created",
  "call_completed",
  "passed_ic",
  "qualified_for_sale",
  "contract_shared",
  "closed_onboarding",
  "no_for_now",
  "ngmi",
  "no_token",
];

function numeric(value: number | string | null | undefined): number | null {
  if (value == null) return null;
  const parsed = typeof value === "number" ? value : Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function formatScore(value: number | string | null | undefined): string {
  const parsed = numeric(value);
  return parsed == null ? "--" : parsed.toFixed(1);
}

function formatDelta(value: number | string | null | undefined): string {
  const parsed = numeric(value);
  if (parsed == null || parsed === 0) return "flat";
  return `${parsed > 0 ? "+" : ""}${parsed.toFixed(1)}`;
}

function formatDate(value: string | null | undefined): string {
  if (!value) return "--";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function formatDateTime(value: string | null | undefined): string {
  if (!value) return "--";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function isoDateDaysFromNow(days: number): string {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return date.toISOString().slice(0, 10);
}

function projectLabel(project: Project): string {
  return project.name?.trim() || `@${project.twitter_handle}`;
}

function projectHandle(project: Project): string {
  return `@${project.twitter_handle}`;
}

function firstUsefulContact(item: EvWorkstationProject): EvIntroContact | null {
  const existingIntro = item.introContacts[0];
  if (existingIntro) return existingIntro;

  const warmMatch = item.warmLead.matches[0];
  if (!warmMatch) return null;

  return {
    id: null,
    projectId: item.project.id,
    workflowId: item.workflow.id,
    contactKey: warmMatch.contactKey,
    fundName: warmMatch.fundName,
    normalizedFundName: warmMatch.normalizedFundName,
    status: "not_asked",
    notes: null,
    isCurrentWarmMatch: true,
    lastStatusChangedAt: null,
    createdAt: null,
    updatedAt: null,
    contact: warmMatch.contact,
  };
}

function queueChipLabels(item: EvWorkstationProject): string[] {
  const chips: string[] = [];
  if (item.flags.projectFollowUpOverdue || item.flags.warmIntroFollowUpOverdue)
    chips.push("overdue");
  if (item.flags.projectFollowUpDueToday || item.flags.warmIntroFollowUpDueToday)
    chips.push("due today");
  if (item.flags.hasWarmLead) chips.push("warm lead");
  if (!item.flags.hasWarmLead) chips.push("no warm lead");
  if ((numeric(item.project.delta_alpha_score) ?? 0) > 0) chips.push("trending up");
  if (item.project.has_tge_hints) chips.push("TGE hint");
  if (item.flags.callToday) chips.push("call today");
  if (item.flags.missingNextAction) chips.push("missing next action");
  return chips.slice(0, 4);
}

function laneCopy(lane: WorkstationLane) {
  return lane === "leads"
    ? {
        title: "Leads",
        eyebrow: "Unworked or lightly worked projects",
        primaryDateLabel: "Warm Intro FU",
      }
    : {
        title: "In Touch",
        eyebrow: "Projects already in active BD motion",
        primaryDateLabel: "Project FU",
      };
}

function WorkstationLaneTabs({
  lane,
  leadCount,
  inTouchCount,
  onLaneChange,
}: Pick<EvWorkstationDashboardProps, "lane" | "leadCount" | "inTouchCount" | "onLaneChange">) {
  return (
    <div className="ev-v2-lane-tabs" data-ev-tour="lane-tabs">
      <button
        type="button"
        className={`ev-v2-lane-tab${lane === "leads" ? " is-active" : ""}`}
        data-ev-tour="lane-tab-leads"
        onClick={() => onLaneChange("leads")}
      >
        Leads <span>{leadCount}</span>
      </button>
      <button
        type="button"
        className={`ev-v2-lane-tab${lane === "in_touch" ? " is-active" : ""}`}
        data-ev-tour="lane-tab-in-touch"
        onClick={() => onLaneChange("in_touch")}
      >
        In Touch <span>{inTouchCount}</span>
      </button>
    </div>
  );
}

function WorkstationQueueRail({
  lane,
  queue,
  activeItem,
  projectSearch,
  onProjectSearchChange,
  onSelectProject,
}: Pick<
  EvWorkstationDashboardProps,
  "lane" | "queue" | "activeItem" | "projectSearch" | "onProjectSearchChange" | "onSelectProject"
>) {
  const copy = laneCopy(lane);
  const [quickFilter, setQuickFilter] = useState<string | null>(null);
  const visibleQueue = quickFilter
    ? queue.filter((item) => queueChipLabels(item).includes(quickFilter))
    : queue;

  return (
    <aside className="ev-workstation-v2__queue ev-v2-panel" data-ev-tour="queue-rail">
      <div className="ev-v2-panel__head" data-ev-tour="queue-title">
        <div>
          <span className="section-title">{copy.title}</span>
          <p className="ev-v2-muted">{copy.eyebrow}</p>
        </div>
        <span className="ev-v2-count">{visibleQueue.length}</span>
      </div>
      <input
        className="leaderboard-search ev-v2-search"
        data-ev-tour="project-search"
        value={projectSearch}
        onChange={(event) => onProjectSearchChange(event.target.value)}
        placeholder="Search projects"
        type="search"
      />
      <div className="ev-v2-filter-chips" data-ev-tour="quick-filter-chips">
        {[
          "overdue",
          "due today",
          "warm lead",
          "trending up",
          "TGE hint",
          "missing next action",
        ].map((chip) => (
          <button
            key={chip}
            type="button"
            className={quickFilter === chip ? "is-active" : ""}
            onClick={() => setQuickFilter((current) => (current === chip ? null : chip))}
          >
            {chip}
          </button>
        ))}
      </div>
      <div className="ev-v2-queue-list" data-ev-tour="queue-list">
        {visibleQueue.length === 0 ? (
          <div className="community-empty queue">
            {quickFilter ? `No ${quickFilter} projects in this lane.` : "No projects in this lane."}
          </div>
        ) : (
          visibleQueue.map((item) => {
            const active = activeItem?.project.id === item.project.id;
            return (
              <button
                type="button"
                key={item.project.id}
                className={`ev-v2-queue-item${active ? " is-active" : ""}`}
                data-ev-tour="queue-item"
                onClick={() => onSelectProject(item.project.id)}
              >
                <span className="ev-v2-queue-item__title">{projectLabel(item.project)}</span>
                <span className="ev-v2-queue-item__meta">
                  {projectHandle(item.project)} · {copy.primaryDateLabel}:{" "}
                  {lane === "leads"
                    ? formatDate(item.workflow.warmIntroFollowUpDate)
                    : formatDate(item.workflow.projectFollowUpDate)}
                </span>
                <span className="ev-v2-queue-item__chips">
                  {queueChipLabels(item).map((chip) => (
                    <span key={chip}>{chip}</span>
                  ))}
                </span>
              </button>
            );
          })
        )}
      </div>
    </aside>
  );
}

interface ProjectCardProps {
  lane: WorkstationLane;
  item: EvWorkstationProject | null;
  isSaving: boolean;
  onNext: () => void;
  onOpenProjectDetail: EvWorkstationDashboardProps["onOpenProjectDetail"];
  onUpdateWorkflow: EvWorkstationDashboardProps["onUpdateWorkflow"];
  onUpdateIntroContact: EvWorkstationDashboardProps["onUpdateIntroContact"];
  onCreateWorkflowEvent: EvWorkstationDashboardProps["onCreateWorkflowEvent"];
  addToast: EvWorkstationDashboardProps["addToast"];
}

function WorkstationProjectCard({
  lane,
  item,
  isSaving,
  onNext,
  onOpenProjectDetail,
  onUpdateWorkflow,
  onUpdateIntroContact,
  onCreateWorkflowEvent,
  addToast,
}: ProjectCardProps) {
  const [nextAction, setNextAction] = useState("");
  const [projectFollowUpDate, setProjectFollowUpDate] = useState("");
  const [groupChatUrl, setGroupChatUrl] = useState("");
  const [internalNotes, setInternalNotes] = useState("");
  const [leadReason, setLeadReason] = useState("");
  const [savingAction, setSavingAction] = useState<string | null>(null);
  const activeProjectKey = item?.project.id ?? null;

  useEffect(() => {
    if (!activeProjectKey) {
      setNextAction("");
      setProjectFollowUpDate("");
      setGroupChatUrl("");
      setInternalNotes("");
      setLeadReason("");
      return;
    }

    setNextAction(item?.workflow.nextAction ?? "");
    setProjectFollowUpDate(item?.workflow.projectFollowUpDate ?? "");
    setGroupChatUrl(item?.workflow.groupChatUrl ?? "");
    setInternalNotes(item?.workflow.internalNotes ?? "");
    setLeadReason("");
  }, [
    activeProjectKey,
    item?.workflow.nextAction,
    item?.workflow.projectFollowUpDate,
    item?.workflow.groupChatUrl,
    item?.workflow.internalNotes,
  ]);

  if (!item) {
    return (
      <main className="ev-workstation-v2__card ev-v2-panel">
        <div className="community-empty queue">Pick a project to start cycling.</div>
      </main>
    );
  }

  const currentItem = item;
  const leadContact = firstUsefulContact(currentItem);
  const leadMode = lane === "leads";

  async function runAction(key: string, fn: () => Promise<void>) {
    setSavingAction(key);
    try {
      await fn();
    } catch (error) {
      addToast(
        "error",
        "Workstation action failed",
        error instanceof Error ? error.message : "Could not save the action."
      );
    } finally {
      setSavingAction(null);
    }
  }

  async function savePlan() {
    await runAction("save-plan", async () => {
      const patch: EvWorkflowPatch = {
        nextAction: nextAction.trim() || null,
        projectFollowUpDate: projectFollowUpDate || null,
        groupChatUrl: groupChatUrl.trim() || null,
        internalNotes: internalNotes.trim() || null,
      };
      await onUpdateWorkflow(currentItem.project.id, patch);
      addToast(
        "success",
        "Project updated",
        `${projectLabel(currentItem.project)} workflow saved.`
      );
    });
  }

  async function markContacted(channel: "telegram" | "twitter" | "other") {
    await runAction(`contacted-${channel}`, async () => {
      await onUpdateWorkflow(currentItem.project.id, {
        lastContactAt: new Date().toISOString(),
        nextTouchpointChannel: channel === "twitter" ? "other" : channel,
        nextAction: nextAction.trim() || null,
        projectFollowUpDate: projectFollowUpDate || isoDateDaysFromNow(7),
        groupChatUrl: groupChatUrl.trim() || null,
      });
      await onCreateWorkflowEvent(currentItem.project.id, {
        eventType: `${channel}_touchpoint_marked`,
        note: `Marked ${channel} follow-up from the workstation card.`,
        source: "manual",
      });
      addToast(
        "success",
        "Touchpoint logged",
        `${projectLabel(currentItem.project)} marked contacted.`
      );
      onNext();
    });
  }

  async function askIntro() {
    if (!leadContact) {
      addToast("info", "No intro contact", "Use contact search to find another intro path.");
      return;
    }

    await runAction("ask-intro", async () => {
      await onUpdateIntroContact(currentItem.project.id, {
        contactKey: leadContact.contactKey,
        fundName: leadContact.fundName,
        normalizedFundName: leadContact.normalizedFundName,
        status: "asked",
        notes: leadReason.trim() || "Asked intro from Leads card.",
        contact: leadContact.contact,
      });
      await onUpdateWorkflow(currentItem.project.id, {
        warmIntroFollowUpDate: isoDateDaysFromNow(7),
        warmIntroFollowUpReason: `Follow up on intro ask with ${leadContact.contact.name}.`,
        nextAction: `Follow up on ${leadContact.fundName} intro`,
      });
      await onCreateWorkflowEvent(currentItem.project.id, {
        eventType: "intro_asked",
        note: `Asked intro via ${leadContact.contact.name}${leadReason.trim() ? `: ${leadReason.trim()}` : "."}`,
        source: "manual",
      });
      addToast(
        "success",
        "Intro ask recorded",
        `${projectLabel(currentItem.project)} moved forward.`
      );
      onNext();
    });
  }

  async function markLeadStage(stage: "no_for_now" | "ngmi") {
    await runAction(stage, async () => {
      await onUpdateWorkflow(currentItem.project.id, {
        stage,
        nextAction: null,
        projectFollowUpDate: null,
      });
      await onCreateWorkflowEvent(currentItem.project.id, {
        eventType: stage === "ngmi" ? "lead_marked_ngmi" : "lead_skipped",
        note: `${stage === "ngmi" ? "Marked NGMI" : "Skipped from lead queue"}${leadReason.trim() ? `: ${leadReason.trim()}` : "."}`,
        source: "manual",
      });
      addToast(
        "success",
        stage === "ngmi" ? "Marked NGMI" : "Skipped",
        `${projectLabel(currentItem.project)} updated.`
      );
      onNext();
    });
  }

  return (
    <main
      className="ev-workstation-v2__card ev-v2-panel"
      aria-busy={isSaving || Boolean(savingAction)}
      data-ev-tour="project-card"
    >
      <div className="ev-v2-project-card__head" data-ev-tour="project-card-header">
        <div>
          <span className="section-title">{leadMode ? "Lead Card" : "In Touch Card"}</span>
          <h2>{projectLabel(item.project)}</h2>
          <a
            className="handle"
            href={`https://twitter.com/${item.project.twitter_handle}`}
            target="_blank"
            rel="noreferrer"
          >
            {projectHandle(item.project)}
          </a>
        </div>
        <div className="ev-v2-project-card__meta">
          <div className="ev-v2-project-card__header-actions" data-ev-tour="project-detail-actions">
            <button
              type="button"
              className="btn btn-small btn-secondary"
              onClick={() =>
                onOpenProjectDetail(item.project, { workflowMode: true, detailView: "full" })
              }
            >
              Full detail
            </button>
            <button
              type="button"
              className="btn btn-small btn-secondary"
              onClick={() =>
                onOpenProjectDetail(item.project, { workflowMode: true, detailView: "call_prep" })
              }
            >
              Call prep
            </button>
          </div>
          <div className="ev-v2-score-stack" data-ev-tour="score-stack">
            <span>{formatScore(item.project.current_alpha_score)}</span>
            <small>{formatDelta(item.project.delta_alpha_score)}</small>
          </div>
        </div>
      </div>

      <div className="ev-v2-card-chips" data-ev-tour="project-card-chips">
        <span>{item.project.category_name ?? "Uncategorized"}</span>
        <span>Stage: {item.workflow.stage.replaceAll("_", " ")}</span>
        {item.project.has_tge_hints ? (
          <span>TGE {item.project.tge_confidence ?? "hint"}</span>
        ) : null}
        {item.flags.hasWarmLead ? <span>Warm lead</span> : <span>No warm lead</span>}
      </div>

      <section className="ev-v2-card-section ev-v2-card-section--why" data-ev-tour="why-surfaced">
        <span className="ev-v2-mini-label">Why surfaced</span>
        <p>
          {leadMode
            ? item.warmLead.summary ||
              queueChipLabels(item).join(" · ") ||
              "Interesting project based on score, trend, and current BD signals."
            : item.workflow.nextAction ||
              item.workflow.callSummary ||
              "Already in touch. Use project follow-up and next action to drive the next move."}
        </p>
      </section>

      {leadMode ? (
        <section className="ev-v2-card-section" data-ev-tour="lead-intro-section">
          <div className="ev-v2-field-grid">
            <div data-ev-tour="lead-intro-status">
              <span className="ev-v2-mini-label">Intro Status</span>
              <strong>{leadContact?.status?.replaceAll("_", " ") ?? "No intro selected"}</strong>
            </div>
            <div>
              <span className="ev-v2-mini-label">Warm Intro FU</span>
              <strong>{formatDate(item.workflow.warmIntroFollowUpDate)}</strong>
            </div>
          </div>
          <div className="ev-v2-intro-list" data-ev-tour="lead-intro-list">
            {(item.introContacts.length > 0
              ? item.introContacts
              : leadContact
                ? [leadContact]
                : []
            ).map((contact) => (
              <div key={contact.contactKey} className="ev-v2-intro-contact">
                <strong>{contact.contact.name}</strong>
                <span>
                  {contact.fundName} · {contact.contact.role ?? "intro route"}
                </span>
                <span className="mono">
                  {contact.contact.telegram ??
                    contact.contact.twitter ??
                    contact.contact.linkedin ??
                    contact.contact.email ??
                    "no URL"}
                </span>
              </div>
            ))}
            {!leadContact ? (
              <div className="community-empty queue">
                No warm route. Use contact search to find another path.
              </div>
            ) : null}
          </div>
          <label className="ev-v2-field" data-ev-tour="lead-reason-note">
            <span>Reason / note</span>
            <textarea
              value={leadReason}
              onChange={(event) => setLeadReason(event.target.value)}
              placeholder="Why ask, skip, or mark NGMI?"
            />
          </label>
          <div className="ev-v2-actions-row" data-ev-tour="lead-actions">
            <button
              type="button"
              className="btn"
              disabled={Boolean(savingAction)}
              onClick={() => void askIntro()}
            >
              {savingAction === "ask-intro" ? "Saving..." : "Ask Intro"}
            </button>
            <button type="button" className="btn btn-secondary" onClick={onNext}>
              Next project
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              disabled={Boolean(savingAction)}
              onClick={() => void markLeadStage("no_for_now")}
            >
              Skip / no for now
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              disabled={Boolean(savingAction)}
              onClick={() => void markLeadStage("ngmi")}
            >
              Not relevant / NGMI
            </button>
          </div>
        </section>
      ) : (
        <section className="ev-v2-card-section" data-ev-tour="in-touch-plan-section">
          <div className="ev-v2-field-grid">
            <label className="ev-v2-field" data-ev-tour="project-follow-up-date">
              <span>Project follow-up</span>
              <input
                type="date"
                value={projectFollowUpDate}
                onChange={(event) => setProjectFollowUpDate(event.target.value)}
              />
            </label>
            <label className="ev-v2-field" data-ev-tour="workflow-stage-select">
              <span>Status / stage</span>
              <select
                value={item.workflow.stage}
                onChange={(event) => {
                  void onUpdateWorkflow(item.project.id, {
                    stage: event.target.value as EvWorkflowStage,
                  });
                }}
              >
                {STAGES.map((stage) => (
                  <option key={stage} value={stage}>
                    {stage.replaceAll("_", " ")}
                  </option>
                ))}
              </select>
            </label>
            <div data-ev-tour="last-contact-next-channel">
              <span className="ev-v2-mini-label">Last contact</span>
              <strong>{formatDateTime(item.workflow.lastContactAt)}</strong>
            </div>
            <div>
              <span className="ev-v2-mini-label">Next channel</span>
              <strong>{item.workflow.nextTouchpointChannel}</strong>
            </div>
          </div>
          <label className="ev-v2-field" data-ev-tour="telegram-group-url">
            <span>Telegram DM/group URL</span>
            <input
              type="url"
              value={groupChatUrl}
              onChange={(event) => setGroupChatUrl(event.target.value)}
              placeholder="https://t.me/..."
            />
          </label>
          <label className="ev-v2-field" data-ev-tour="next-action">
            <span>Next action</span>
            <textarea
              value={nextAction}
              onChange={(event) => setNextAction(event.target.value)}
              placeholder="Concrete next move"
            />
          </label>
          <label className="ev-v2-field" data-ev-tour="internal-notes">
            <span>Notes</span>
            <textarea
              value={internalNotes}
              onChange={(event) => setInternalNotes(event.target.value)}
              placeholder="Internal context, blockers, or latest touchpoint"
            />
          </label>
          <div className="ev-v2-actions-row" data-ev-tour="in-touch-actions">
            <button
              type="button"
              className="btn"
              disabled={Boolean(savingAction)}
              onClick={() => void savePlan()}
            >
              {savingAction === "save-plan" ? "Saving..." : "Update Status"}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              disabled={Boolean(savingAction)}
              onClick={() => void markContacted("telegram")}
            >
              Follow up / mark Telegram
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              disabled={Boolean(savingAction)}
              onClick={() => void markContacted("twitter")}
            >
              Mark X touchpoint
            </button>
            <button type="button" className="btn btn-secondary" onClick={onNext}>
              Next project
            </button>
          </div>
          {item.introContacts.length > 0 ? (
            <details className="ev-v2-intro-history" data-ev-tour="intro-history">
              <summary>Relationship source / intro history</summary>
              <div className="ev-v2-intro-list">
                {item.introContacts.map((contact) => (
                  <div key={contact.contactKey} className="ev-v2-intro-contact">
                    <strong>{contact.contact.name}</strong>
                    <span>
                      {contact.fundName} · {contact.status.replaceAll("_", " ")}
                    </span>
                  </div>
                ))}
              </div>
            </details>
          ) : null}
        </section>
      )}
    </main>
  );
}

function identityText(contact: EvContactSearchResult): string {
  const identities = contact.platformIdentities
    .map((identity) => identity.identityValue ?? identity.canonicalUrl ?? identity.platform)
    .filter(Boolean);
  return identities.slice(0, 3).join(" · ") || "No identity saved";
}

function contactRelationships(contact: EvContactSearchResult): EvContactRelationshipSummary[] {
  const byId = new Map<string, EvContactRelationshipSummary>();
  byId.set(contact.relationship.id, contact.relationship);
  for (const relationship of contact.relationships) {
    byId.set(relationship.id, relationship);
  }
  return [...byId.values()];
}

function findProjectRelationship(
  contact: EvContactSearchResult,
  projectId: string | null
): EvContactRelationshipSummary | null {
  if (!projectId) return null;
  return (
    contactRelationships(contact).find(
      (relationship) => relationship.targetType === "project" && relationship.targetId === projectId
    ) ?? null
  );
}

function findContactRelationshipById(
  contacts: EvContactSearchResult[] | undefined,
  relationshipId: string | null
): { contact: EvContactSearchResult; relationship: EvContactRelationshipSummary } | null {
  if (!contacts || !relationshipId) return null;
  for (const contact of contacts) {
    const relationship = contactRelationships(contact).find((item) => item.id === relationshipId);
    if (relationship) return { contact, relationship };
  }
  return null;
}

function extractXHandleFromContact(contact: EvContactSearchResult | null): string | null {
  const identity = contact?.platformIdentities.find(
    (item) => item.platform === "twitter" || item.platform === "x"
  );
  const raw = identity?.identityValue ?? identity?.canonicalUrl;
  if (!raw) return null;
  const handle = raw
    .trim()
    .replace(/^https?:\/\/(?:www\.)?(?:x|twitter)\.com\//iu, "")
    .replace(/^@/u, "")
    .split(/[/?#]/u)[0]
    ?.trim();
  return handle && /^[A-Za-z0-9_]{1,15}$/u.test(handle) ? handle : null;
}

function WorkstationActionDock({
  activeItem,
  contactSearch,
  onContactSearchChange,
  onUpdateWorkflow,
  onCreateWorkflowEvent,
  onNext,
  todayBriefNode,
  googleStatusNode,
  addToast,
}: Pick<
  EvWorkstationDashboardProps,
  | "activeItem"
  | "contactSearch"
  | "onContactSearchChange"
  | "onUpdateWorkflow"
  | "onCreateWorkflowEvent"
  | "onNext"
  | "todayBriefNode"
  | "googleStatusNode"
  | "addToast"
>) {
  const [contactOwnerScopeFilter, setContactOwnerScopeFilter] = useState<
    "all" | "personal" | "company"
  >("all");
  const deferredContactSearch = useDeferredValue(contactSearch);
  const contactsQuery = useEvContactsQuery(true, {
    search: deferredContactSearch,
    ownerScope: contactOwnerScopeFilter,
    limit: 12,
  });
  const telegramCaptureMutation = useCaptureTelegramRelationshipMutation();
  const twitterCaptureMutation = useCaptureTwitterRelationshipMutation();
  const relationshipUpdateMutation = useUpdateOutreachRelationshipMutation();
  const relationshipTargetMutation = useUpdateOutreachRelationshipTargetMutation();
  const linkedInAttachMutation = useAttachLinkedInIdentityMutation();
  const followUpMutation = useUpdateRelationshipFollowUpMutation();
  const telegramStatusQuery = useTelegramStatusQuery(true);
  const telegramAuthMutations = useTelegramAuthMutations();
  const telegramSendMutation = useTelegramSendMutation();
  const xStatusQuery = useXConnectionStatusQuery(true);
  const xPromptSettingsQuery = useXPromptSettingsQuery(true);
  const startXConnectMutation = useStartXConnectMutation();
  const disconnectXMutation = useDisconnectXMutation();
  const saveXPromptSettingsMutation = useSaveXPromptSettingsMutation();
  const xPromptExamplesMutation = useGenerateXPromptExamplesMutation();
  const xResolveHandleMutation = useXResolveHandleMutation();
  const xPreviewMutation = useXPreviewMessageMutation();
  const xSendMutation = useXSendMessageMutation();
  const xQueueContactsQuery = useXQueueContactsQuery(true, {
    search: deferredContactSearch,
    limit: 5,
  });
  const xQueueDraftsQuery = useXQueueDraftsQuery(true, { status: "previewed" });
  const xQueuePreviewMutation = useXQueuePreviewMutation();
  const xQueueSendMutation = useXQueueSendMutation();
  const discardXQueueDraftMutation = useDiscardXQueueDraftMutation();
  const repairXQueueDraftMutation = useRepairXQueueDraftMutation();
  const recordTouchpointsMutation = useRecordTouchpointsMutation();
  const appendMessageLogMutation = useAppendMessageLogMutation();
  const [pastedUrl, setPastedUrl] = useState("");
  const [bareHandlePlatform, setBareHandlePlatform] = useState<"telegram" | "x" | "">("");
  const [selectedRelationshipId, setSelectedRelationshipId] = useState<string | null>(null);
  const [contactFullName, setContactFullName] = useState("");
  const [contactTitle, setContactTitle] = useState("");
  const [contactCompany, setContactCompany] = useState("");
  const [contactTargetLabel, setContactTargetLabel] = useState("");
  const [contactOwnerScope, setContactOwnerScope] = useState<"personal" | "company">("company");
  const [contactIsIntro, setContactIsIntro] = useState(false);
  const [relationshipFollowUpDate, setRelationshipFollowUpDate] = useState("");
  const [xPromptTemplate, setXPromptTemplate] = useState("");
  const [xPromptExamples, setXPromptExamples] = useState<
    Array<{ projectId: string; prompt: string }>
  >([]);
  const [telegramApiId, setTelegramApiId] = useState("");
  const [telegramApiHash, setTelegramApiHash] = useState("");
  const [telegramPhone, setTelegramPhone] = useState("");
  const [telegramCode, setTelegramCode] = useState("");
  const [telegramPassword, setTelegramPassword] = useState("");
  const [telegramChatUrl, setTelegramChatUrl] = useState("");
  const [telegramBody, setTelegramBody] = useState("");
  const [xBody, setXBody] = useState("");
  const [xPreviewedDraft, setXPreviewedDraft] = useState<string | null>(null);
  const [xPreviewSimilarityBlocked, setXPreviewSimilarityBlocked] = useState(false);
  const [xResolvedHandle, setXResolvedHandle] = useState<EvXResolveHandlePayload | null>(null);
  const activeProjectId = activeItem?.project.id ?? null;
  const activeGroupChatUrl = activeItem?.workflow.groupChatUrl ?? "";
  const activeProjectResetKey = `${activeProjectId ?? "none"}:${activeGroupChatUrl}`;

  const parsed = useMemo(
    () => parseContactUrl(pastedUrl, bareHandlePlatform ? { bareHandlePlatform } : {}),
    [pastedUrl, bareHandlePlatform]
  );
  const isAmbiguous = isBareAmbiguousHandle(pastedUrl) && !bareHandlePlatform;

  useEffect(() => {
    if (!isBareAmbiguousHandle(pastedUrl)) setBareHandlePlatform("");
  }, [pastedUrl]);

  useEffect(() => {
    if (!activeProjectResetKey) return;
    setSelectedRelationshipId(null);
    setTelegramChatUrl(activeGroupChatUrl);
    setTelegramBody("");
    setXBody("");
    setXPreviewedDraft(null);
    setXPreviewSimilarityBlocked(false);
    setXResolvedHandle(null);
  }, [activeProjectResetKey, activeGroupChatUrl]);

  useEffect(() => {
    if (selectedRelationshipId) return;
    const first = contactsQuery.data
      ?.map((contact) => findProjectRelationship(contact, activeProjectId))
      .find((relationship): relationship is EvContactRelationshipSummary => Boolean(relationship));
    if (first) setSelectedRelationshipId(first.id);
  }, [activeProjectId, contactsQuery.data, selectedRelationshipId]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      const payload = event.data as { type?: string; status?: string; message?: string } | null;
      if (!payload || payload.type !== "legion-x-oauth") return;
      void xStatusQuery.refetch();
      addToast(
        payload.status === "success" ? "success" : "error",
        payload.status === "success" ? "X connected" : "X connection failed",
        payload.message ?? "X connection flow finished."
      );
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [addToast, xStatusQuery]);

  const defaultRelationshipId = activeProjectId
    ? (contactsQuery.data
        ?.map((contact) => findProjectRelationship(contact, activeProjectId))
        .find((relationship): relationship is EvContactRelationshipSummary => Boolean(relationship))
        ?.id ?? null)
    : null;
  const effectiveSelectedRelationshipId = selectedRelationshipId ?? defaultRelationshipId;
  const selectedContactRelationship = findContactRelationshipById(
    contactsQuery.data,
    effectiveSelectedRelationshipId
  );
  const selectedContact = selectedContactRelationship?.contact ?? null;
  const selectedRelationship = selectedContactRelationship?.relationship ?? null;
  const selectedXHandle = extractXHandleFromContact(selectedContact);
  const xThreadQuery = useXThreadQuery(
    effectiveSelectedRelationshipId,
    Boolean(effectiveSelectedRelationshipId)
  );
  const xAuditQuery = useXAuditTrailQuery(
    effectiveSelectedRelationshipId,
    Boolean(effectiveSelectedRelationshipId)
  );

  useEffect(() => {
    if (!selectedContact || !selectedRelationship) {
      setContactFullName("");
      setContactTitle("");
      setContactCompany("");
      setContactTargetLabel("");
      setContactOwnerScope("company");
      setContactIsIntro(false);
      setRelationshipFollowUpDate("");
      return;
    }

    setContactFullName(selectedContact.person.fullName);
    setContactTitle(selectedContact.person.title ?? "");
    setContactCompany(selectedContact.person.companyName ?? "");
    setContactTargetLabel(selectedRelationship.targetLabel);
    setContactOwnerScope(selectedRelationship.ownerScope);
    setContactIsIntro(selectedRelationship.isIntroContact);
    setRelationshipFollowUpDate("");
  }, [selectedContact, selectedRelationship]);

  useEffect(() => {
    setXPromptTemplate(xPromptSettingsQuery.data?.template ?? "");
  }, [xPromptSettingsQuery.data?.template]);

  function requireSelectedRelationship(): string | null {
    if (!effectiveSelectedRelationshipId) {
      addToast("info", "Select a contact", "Choose a relationship in contact search first.");
      return null;
    }
    return effectiveSelectedRelationshipId;
  }

  async function logOutboundMessage(input: {
    relationshipId: string;
    channel: "telegram" | "twitter";
    body: string;
    externalUrl?: string;
  }) {
    const touchpoint: EvTouchpointRequest = {
      relationshipId: input.relationshipId,
      channel: input.channel,
      touchpointType: "dm_sent",
      direction: "outbound",
      body: input.body,
      externalUrl: input.externalUrl,
      outcome: "sent",
      ownerScope: "company",
    };
    const message: EvMessageLogRequest = {
      relationshipId: input.relationshipId,
      channel: input.channel,
      direction: "outbound",
      body: input.body,
      sentAt: new Date().toISOString(),
      source: "ev_workstation",
      ownerScope: "company",
    };
    await recordTouchpointsMutation.mutateAsync(touchpoint);
    await appendMessageLogMutation.mutateAsync(message);
  }

  async function saveSelectedContactDetails() {
    if (!selectedContact || !selectedRelationship) {
      addToast("info", "Select a contact", "Choose a relationship before editing details.");
      return;
    }

    try {
      await relationshipUpdateMutation.mutateAsync({
        relationshipId: selectedRelationship.id,
        projectId: selectedRelationship.targetId,
        body: {
          fullName: contactFullName.trim() || selectedContact.person.fullName,
          title: contactTitle.trim() || null,
          companyName: contactCompany.trim() || null,
          targetLabel: contactTargetLabel.trim() || selectedRelationship.targetLabel,
          ownerScope: contactOwnerScope,
          isIntroContact: contactIsIntro,
        },
      });
      addToast("success", "Contact updated", `${contactFullName || "Contact"} saved.`);
    } catch (error) {
      addToast(
        "error",
        "Contact update failed",
        error instanceof Error ? error.message : "Could not update contact details."
      );
    }
  }

  async function moveSelectedContactToActiveProject() {
    if (!selectedContact || !selectedRelationship || !activeItem) {
      addToast("info", "Select contact and project", "Pick a contact and active project first.");
      return;
    }

    try {
      await relationshipTargetMutation.mutateAsync({
        relationshipId: selectedRelationship.id,
        body: {
          targetType: "project",
          targetId: activeItem.project.id,
          targetLabel: projectLabel(activeItem.project),
        },
      });
      addToast(
        "success",
        "Relationship retargeted",
        `${selectedContact.person.fullName} now points at ${projectLabel(activeItem.project)}.`
      );
    } catch (error) {
      addToast(
        "error",
        "Target update failed",
        error instanceof Error ? error.message : "Could not retarget the relationship."
      );
    }
  }

  async function saveRelationshipFollowUp() {
    if (!selectedContact || !selectedRelationship) {
      addToast("info", "Select a contact", "Choose a relationship before saving follow-up.");
      return;
    }
    if (!relationshipFollowUpDate) {
      addToast("info", "Follow-up date needed", "Pick a follow-up date for this relationship.");
      return;
    }

    try {
      await followUpMutation.mutateAsync({
        relationshipId: selectedRelationship.id,
        followUpDate: relationshipFollowUpDate,
      });
      addToast(
        "success",
        "Relationship follow-up saved",
        `Follow-up set for ${relationshipFollowUpDate}.`
      );
    } catch (error) {
      addToast(
        "error",
        "Follow-up update failed",
        error instanceof Error ? error.message : "Could not update relationship follow-up."
      );
    }
  }

  async function saveXPromptTemplate() {
    try {
      await saveXPromptSettingsMutation.mutateAsync(xPromptTemplate);
      addToast("success", "X prompt saved", "Future previews use the updated template.");
    } catch (error) {
      addToast(
        "error",
        "X prompt save failed",
        error instanceof Error ? error.message : "Could not save X prompt settings."
      );
    }
  }

  async function generateXPromptExamplesForActiveProject() {
    if (!activeItem) {
      addToast("info", "Pick a project", "Select a project before generating prompt examples.");
      return;
    }

    try {
      const result = await xPromptExamplesMutation.mutateAsync([activeItem.project.id]);
      setXPromptExamples(result?.examples ?? []);
      addToast(
        "success",
        "X prompt examples generated",
        "Review the example before saving prompt changes."
      );
    } catch (error) {
      addToast(
        "error",
        "Prompt examples failed",
        error instanceof Error ? error.message : "Could not generate X prompt examples."
      );
    }
  }

  async function resolveSelectedXHandle(): Promise<boolean> {
    if (!selectedXHandle) {
      addToast("info", "No X handle", "Select or attach a contact with an X handle first.");
      return false;
    }

    try {
      const result = await xResolveHandleMutation.mutateAsync(selectedXHandle);
      setXResolvedHandle(result);
      return true;
    } catch (error) {
      addToast(
        "error",
        "X handle resolve failed",
        error instanceof Error ? error.message : "Could not resolve this X handle."
      );
      return false;
    }
  }

  async function previewQueuedXContact(relationshipId: string) {
    try {
      const preview = await xQueuePreviewMutation.mutateAsync({ relationshipId });
      setSelectedRelationshipId(relationshipId);
      setXBody(preview?.draft ?? "");
      setXPreviewedDraft(preview?.draft ?? null);
      setXPreviewSimilarityBlocked(preview?.similarityBlocked === true);
      addToast("success", "Queued X draft ready", "Review/edit the queued draft before sending.");
    } catch (error) {
      addToast(
        "error",
        "Queue preview failed",
        error instanceof Error ? error.message : "Could not preview queued X DM."
      );
    }
  }

  async function sendQueuedXDraft(draftId: string, body?: string | null) {
    try {
      const result = await xQueueSendMutation.mutateAsync({
        draftId,
        body: body ?? undefined,
        sendSource: "ev_workstation_queue",
      });
      if (result?.relationshipId && body) {
        await logOutboundMessage({
          relationshipId: result.relationshipId,
          channel: "twitter",
          body,
        });
      }
      addToast("success", "Queued X DM sent", "Draft sent and removed from the preview queue.");
    } catch (error) {
      addToast(
        "error",
        "Queued X send failed",
        error instanceof Error ? error.message : "Could not send queued X DM."
      );
    }
  }

  async function sendTelegramFollowUp() {
    if (!activeItem) return;
    const relationshipId = requireSelectedRelationship();
    if (!relationshipId) return;
    const chatUrl = telegramChatUrl.trim() || activeItem.workflow.groupChatUrl?.trim();
    if (!chatUrl) {
      addToast("info", "Telegram URL needed", "Paste or save a Telegram DM/group URL first.");
      return;
    }
    const body = telegramBody.trim();
    if (!body) {
      addToast("info", "Message needed", "Write the Telegram follow-up before sending.");
      return;
    }

    try {
      await telegramSendMutation.mutateAsync({ chatUrl, text: body });
      await logOutboundMessage({ relationshipId, channel: "telegram", body, externalUrl: chatUrl });
      await onUpdateWorkflow(activeItem.project.id, {
        lastContactAt: new Date().toISOString(),
        projectFollowUpDate: isoDateDaysFromNow(7),
        nextTouchpointChannel: "telegram",
        groupChatUrl: chatUrl,
      });
      await onCreateWorkflowEvent(activeItem.project.id, {
        eventType: "telegram_message_sent",
        note: `Sent Telegram follow-up: ${body.slice(0, 160)}`,
        source: "manual",
      });
      addToast("success", "Telegram sent", `${projectLabel(activeItem.project)} follow-up sent.`);
      setTelegramBody("");
      onNext();
    } catch (error) {
      addToast(
        "error",
        "Telegram send failed",
        error instanceof Error ? error.message : "Could not send Telegram message."
      );
    }
  }

  async function previewXFollowUp() {
    if (!activeItem) return;
    const relationshipId = requireSelectedRelationship();
    if (!relationshipId) return;
    if (!(await resolveSelectedXHandle())) return;
    try {
      const preview = await xPreviewMutation.mutateAsync({
        relationshipId,
        projectId: activeItem.project.id,
      });
      setXBody(preview?.draft ?? "");
      setXPreviewedDraft(preview?.draft ?? null);
      setXPreviewSimilarityBlocked(preview?.similarityBlocked === true);
      addToast(
        preview?.similarityBlocked ? "info" : "success",
        preview?.similarityBlocked ? "X draft needs repair" : "X draft ready",
        preview?.recommendedRepair ?? "Review/edit the draft before sending."
      );
    } catch (error) {
      addToast(
        "error",
        "X preview failed",
        error instanceof Error ? error.message : "Could not preview X DM."
      );
    }
  }

  async function startXConnect() {
    const popup = window.open(
      "about:blank",
      `legion-x-connect-${Date.now()}`,
      "popup,width=640,height=760"
    );
    if (!popup) {
      addToast("error", "Popup blocked", "Allow popups to complete X OAuth.");
      return;
    }

    try {
      const payload = await startXConnectMutation.mutateAsync();
      if (!payload?.authorizationUrl) throw new Error("X authorization URL was not returned");
      popup.location.href = payload.authorizationUrl;
    } catch (error) {
      popup.close();
      addToast(
        "error",
        "X connect failed",
        error instanceof Error ? error.message : "Could not start X OAuth."
      );
    }
  }

  async function sendXFollowUp() {
    if (!activeItem) return;
    const relationshipId = requireSelectedRelationship();
    if (!relationshipId) return;
    if (!(await resolveSelectedXHandle())) return;
    const body = xBody.trim();
    if (!body) {
      addToast("info", "X message needed", "Preview or write an X DM before sending.");
      return;
    }

    try {
      await xSendMutation.mutateAsync({
        relationshipId,
        projectId: activeItem.project.id,
        body,
        previewedDraft: xPreviewedDraft,
        reviewedByHuman: true,
        sendSource: "ev_workstation",
      });
      await logOutboundMessage({ relationshipId, channel: "twitter", body });
      await onUpdateWorkflow(activeItem.project.id, {
        lastContactAt: new Date().toISOString(),
        projectFollowUpDate: isoDateDaysFromNow(7),
        nextTouchpointChannel: "other",
      });
      await onCreateWorkflowEvent(activeItem.project.id, {
        eventType: "x_message_sent",
        note: `Sent X DM: ${body.slice(0, 160)}`,
        source: "manual",
      });
      addToast("success", "X DM sent", `${projectLabel(activeItem.project)} follow-up sent.`);
      setXBody("");
      setXPreviewedDraft(null);
      setXPreviewSimilarityBlocked(false);
      onNext();
    } catch (error) {
      addToast(
        "error",
        "X send failed",
        error instanceof Error ? error.message : "Could not send X DM."
      );
    }
  }

  async function attachParsedContact() {
    if (!activeItem) return;
    const targetLabel = projectLabel(activeItem.project);

    try {
      if (parsed.platform === "telegram") {
        await telegramCaptureMutation.mutateAsync({
          chatUrl: parsed.canonicalUrl,
          chatTitle: parsed.handle ? `@${parsed.handle}` : targetLabel,
          chatType: parsed.kind === "unknown" ? "dm" : parsed.kind,
          telegramHandle: parsed.handle,
          targetType: "project",
          targetId: activeItem.project.id,
          targetLabel,
        });
        addToast(
          "success",
          "Telegram attached",
          `${parsed.canonicalUrl} attached to ${targetLabel}.`
        );
        setPastedUrl("");
        return;
      }

      if (parsed.platform === "x") {
        await twitterCaptureMutation.mutateAsync({
          twitterHandle: parsed.handle,
          displayName: parsed.handle,
          targetType: "project",
          targetId: activeItem.project.id,
          targetLabel,
        });
        addToast("success", "X attached", `@${parsed.handle} attached to ${targetLabel}.`);
        setPastedUrl("");
        return;
      }

      if (parsed.platform === "linkedin") {
        if (!selectedContact || !selectedRelationship) {
          addToast("info", "Select a contact", "Pick a relationship before attaching LinkedIn.");
          return;
        }
        await linkedInAttachMutation.mutateAsync({
          relationshipId: selectedRelationship.id,
          body: {
            personId: selectedContact.person.id,
            profileUrl: parsed.canonicalUrl,
            fullName: contactFullName.trim() || selectedContact.person.fullName,
            title: contactTitle.trim() || selectedContact.person.title,
            companyName: contactCompany.trim() || selectedContact.person.companyName,
            replaceExistingLinkedIn: true,
            fieldUpdates: {
              updateName: Boolean(contactFullName.trim()),
              updateTitle: Boolean(contactTitle.trim()),
              updateCompany: Boolean(contactCompany.trim()),
            },
          },
        });
        addToast(
          "success",
          "LinkedIn attached",
          `${parsed.canonicalUrl} attached to ${selectedContact.person.fullName}.`
        );
        setPastedUrl("");
        return;
      }

      addToast("info", "Unsupported URL", "Choose a supported Telegram, X, or LinkedIn URL.");
    } catch (error) {
      addToast(
        "error",
        "Contact attach failed",
        error instanceof Error ? error.message : "Could not attach the URL."
      );
    }
  }

  return (
    <aside className="ev-workstation-v2__actions ev-v2-panel" data-ev-tour="action-dock">
      <div className="ev-v2-panel__head">
        <div>
          <span className="section-title">Action Dock</span>
          <p className="ev-v2-muted">
            Search contacts, paste URLs, and keep messaging context close.
          </p>
        </div>
      </div>

      <section className="ev-v2-dock-section" data-ev-tour="contact-search-section">
        <div className="ev-v2-field-grid">
          <label className="ev-v2-field">
            <span>Precise contact search</span>
            <input
              type="search"
              data-ev-tour="contact-search"
              value={contactSearch}
              onChange={(event) => onContactSearchChange(event.target.value)}
              placeholder="Name, company, title, Telegram, X, LinkedIn, email"
            />
          </label>
          <label className="ev-v2-field" data-ev-tour="contact-scope-filter">
            <span>Scope</span>
            <select
              value={contactOwnerScopeFilter}
              onChange={(event) =>
                setContactOwnerScopeFilter(event.target.value as "all" | "personal" | "company")
              }
            >
              <option value="all">Company + mine</option>
              <option value="company">Company</option>
              <option value="personal">Personal</option>
            </select>
          </label>
        </div>
        <div className="ev-v2-contact-results" data-ev-tour="contact-results">
          {contactsQuery.isLoading ? (
            <div className="ev-v2-muted">Searching contacts...</div>
          ) : contactsQuery.data?.length ? (
            contactsQuery.data.map((contact) => {
              const displayRelationship =
                findProjectRelationship(contact, activeProjectId) ?? contact.relationship;
              const active = effectiveSelectedRelationshipId === displayRelationship.id;
              return (
                <button
                  key={displayRelationship.id}
                  type="button"
                  className={`ev-v2-contact-result${active ? " is-active" : ""}`}
                  onClick={() => {
                    setSelectedRelationshipId(displayRelationship.id);
                    const telegramIdentity = contact.platformIdentities.find(
                      (identity) => identity.platform === "telegram"
                    );
                    const twitterIdentity = contact.platformIdentities.find(
                      (identity) => identity.platform === "twitter" || identity.platform === "x"
                    );
                    if (telegramIdentity?.canonicalUrl)
                      setTelegramChatUrl(telegramIdentity.canonicalUrl);
                    if (twitterIdentity?.identityValue && !xBody) {
                      setXBody(`Hey ${contact.person.fullName.split(/\s+/u)[0] ?? "there"} - `);
                    }
                  }}
                >
                  <strong>{contact.person.fullName}</strong>
                  <span>
                    {contact.person.title ?? "No title"}{" "}
                    {contact.person.companyName ? `· ${contact.person.companyName}` : ""}
                  </span>
                  <span className="mono">{identityText(contact)}</span>
                  <small>
                    {displayRelationship.targetLabel} ·{" "}
                    {displayRelationship.ownerScope ?? "company"}
                  </small>
                </button>
              );
            })
          ) : (
            <div className="community-empty queue">No matching contacts yet.</div>
          )}
        </div>
      </section>

      <section className="ev-v2-dock-section" data-ev-tour="contact-editor">
        <div className="ev-v2-panel__head ev-v2-panel__head--compact">
          <div>
            <span className="ev-v2-mini-label">Contact detail editor</span>
            <p className="ev-v2-muted">
              Update canonical contact fields, scope, target project, and follow-up.
            </p>
          </div>
        </div>
        {selectedContact ? (
          <>
            <div className="ev-v2-field-grid" data-ev-tour="contact-editor-fields">
              <label className="ev-v2-field">
                <span>Name</span>
                <input
                  value={contactFullName}
                  onChange={(event) => setContactFullName(event.target.value)}
                />
              </label>
              <label className="ev-v2-field">
                <span>Title</span>
                <input
                  value={contactTitle}
                  onChange={(event) => setContactTitle(event.target.value)}
                />
              </label>
              <label className="ev-v2-field">
                <span>Company</span>
                <input
                  value={contactCompany}
                  onChange={(event) => setContactCompany(event.target.value)}
                />
              </label>
              <label className="ev-v2-field">
                <span>Target label</span>
                <input
                  value={contactTargetLabel}
                  onChange={(event) => setContactTargetLabel(event.target.value)}
                />
              </label>
              <label className="ev-v2-field">
                <span>Scope</span>
                <select
                  value={contactOwnerScope}
                  onChange={(event) =>
                    setContactOwnerScope(event.target.value as "personal" | "company")
                  }
                >
                  <option value="company">Company</option>
                  <option value="personal">Personal</option>
                </select>
              </label>
              <label className="ev-v2-field">
                <span>Relationship FU</span>
                <input
                  type="date"
                  value={relationshipFollowUpDate}
                  onChange={(event) => setRelationshipFollowUpDate(event.target.value)}
                />
              </label>
            </div>
            <label className="ev-v2-checkbox-row">
              <input
                type="checkbox"
                checked={contactIsIntro}
                onChange={(event) => setContactIsIntro(event.target.checked)}
              />
              Intro contact
            </label>
            <div className="ev-v2-actions-row" data-ev-tour="contact-editor-actions">
              <button
                type="button"
                className="btn btn-small"
                disabled={relationshipUpdateMutation.isPending}
                onClick={() => void saveSelectedContactDetails()}
              >
                {relationshipUpdateMutation.isPending ? "Saving..." : "Save contact"}
              </button>
              <button
                type="button"
                className="btn btn-small btn-secondary"
                disabled={!activeItem || relationshipTargetMutation.isPending}
                onClick={() => void moveSelectedContactToActiveProject()}
              >
                Move to active project
              </button>
              <button
                type="button"
                className="btn btn-small btn-secondary"
                disabled={!relationshipFollowUpDate || followUpMutation.isPending}
                onClick={() => void saveRelationshipFollowUp()}
              >
                Save relationship FU
              </button>
            </div>
          </>
        ) : (
          <div className="community-empty queue">Select a contact to edit canonical details.</div>
        )}
      </section>

      <section className="ev-v2-dock-section">
        <label className="ev-v2-field" data-ev-tour="paste-contact-url">
          <span>Paste contact URL</span>
          <input
            type="text"
            value={pastedUrl}
            onChange={(event) => setPastedUrl(event.target.value)}
            placeholder="t.me/founder, x.com/founder, LinkedIn URL, @handle"
          />
        </label>
        {isAmbiguous ? (
          <div className="ev-v2-ambiguous-box">
            <span>Bare @handle is ambiguous. Choose platform:</span>
            <button
              type="button"
              className="btn btn-small btn-secondary"
              onClick={() => setBareHandlePlatform("telegram")}
            >
              Telegram
            </button>
            <button
              type="button"
              className="btn btn-small btn-secondary"
              onClick={() => setBareHandlePlatform("x")}
            >
              X
            </button>
          </div>
        ) : null}
        {pastedUrl.trim() ? (
          <div className="ev-v2-parsed-url" data-ev-tour="detected-contact-url">
            <span className="ev-v2-mini-label">Detected</span>
            <strong>{parsed.platform}</strong>
            {parsed.platform !== "unknown" ? (
              <span className="mono">{parsed.canonicalUrl}</span>
            ) : (
              <span>Choose a supported Telegram, X, or LinkedIn URL.</span>
            )}
          </div>
        ) : null}
        <button
          type="button"
          className="btn btn-secondary"
          disabled={
            !activeItem ||
            parsed.platform === "unknown" ||
            (parsed.platform === "linkedin" && !selectedContact) ||
            isAmbiguous ||
            telegramCaptureMutation.isPending ||
            twitterCaptureMutation.isPending ||
            linkedInAttachMutation.isPending
          }
          onClick={() => void attachParsedContact()}
        >
          {telegramCaptureMutation.isPending ||
          twitterCaptureMutation.isPending ||
          linkedInAttachMutation.isPending
            ? "Attaching..."
            : parsed.platform === "linkedin"
              ? "Attach to selected contact"
              : "Attach to active project"}
        </button>
      </section>

      <section className="ev-v2-dock-section" data-ev-tour="telegram-send-section">
        <div className="ev-v2-panel__head ev-v2-panel__head--compact">
          <div>
            <span className="ev-v2-mini-label">Telegram Send</span>
            <p className="ev-v2-muted">
              {telegramStatusQuery.data?.connected
                ? "MTProto connected. Sends from the operator account."
                : telegramStatusQuery.data?.configured
                  ? `Telegram ${telegramStatusQuery.data.connectionStatus}. Connect before sending.`
                  : "Telegram MTProto not configured."}
            </p>
          </div>
        </div>
        {telegramStatusQuery.data?.connected ? (
          <button
            type="button"
            className="btn btn-small btn-secondary"
            disabled={telegramAuthMutations.disconnect.isPending}
            onClick={() => {
              telegramAuthMutations.disconnect.mutate(undefined, {
                onSuccess: () =>
                  addToast("info", "Telegram disconnected", "MTProto session cleared."),
                onError: (error) =>
                  addToast(
                    "error",
                    "Telegram disconnect failed",
                    error instanceof Error ? error.message : "Could not disconnect Telegram."
                  ),
              });
            }}
          >
            {telegramAuthMutations.disconnect.isPending
              ? "Disconnecting..."
              : "Disconnect Telegram"}
          </button>
        ) : null}
        {!telegramStatusQuery.data?.connected ? (
          <details className="ev-v2-details" data-ev-tour="telegram-connection-details">
            <summary>Telegram connection</summary>
            <div className="ev-v2-field-grid">
              <label className="ev-v2-field">
                <span>API ID</span>
                <input
                  value={telegramApiId}
                  onChange={(event) => setTelegramApiId(event.target.value)}
                />
              </label>
              <label className="ev-v2-field">
                <span>API Hash</span>
                <input
                  value={telegramApiHash}
                  onChange={(event) => setTelegramApiHash(event.target.value)}
                />
              </label>
            </div>
            <label className="ev-v2-field">
              <span>Phone number</span>
              <input
                value={telegramPhone}
                onChange={(event) => setTelegramPhone(event.target.value)}
                placeholder="+15551234567"
              />
            </label>
            <div className="ev-v2-actions-row">
              <button
                type="button"
                className="btn btn-small btn-secondary"
                disabled={telegramAuthMutations.startAuth.isPending}
                onClick={() => {
                  telegramAuthMutations.startAuth.mutate(
                    {
                      apiId: Number(telegramApiId),
                      apiHash: telegramApiHash,
                      phoneNumber: telegramPhone,
                    },
                    {
                      onSuccess: () => {
                        setTelegramCode("");
                        setTelegramPassword("");
                        addToast(
                          "info",
                          "Telegram code sent",
                          "Enter the login code to verify MTProto."
                        );
                      },
                      onError: (error) =>
                        addToast(
                          "error",
                          "Telegram auth failed",
                          error instanceof Error ? error.message : "Could not start Telegram auth."
                        ),
                    }
                  );
                }}
              >
                Start Telegram Auth
              </button>
            </div>
            <label className="ev-v2-field">
              <span>Login code</span>
              <input
                value={telegramCode}
                onChange={(event) => setTelegramCode(event.target.value)}
              />
            </label>
            <label className="ev-v2-field">
              <span>2FA password (if Telegram asks)</span>
              <input
                type="password"
                value={telegramPassword}
                onChange={(event) => setTelegramPassword(event.target.value)}
                placeholder="Optional"
              />
            </label>
            <button
              type="button"
              className="btn btn-small btn-secondary"
              disabled={telegramAuthMutations.verifyAuth.isPending || !telegramCode.trim()}
              onClick={() => {
                telegramAuthMutations.verifyAuth.mutate(
                  { code: telegramCode, password: telegramPassword.trim() || null },
                  {
                    onSuccess: () => {
                      setTelegramPassword("");
                      addToast("success", "Telegram connected", "MTProto session saved.");
                    },
                    onError: (error) =>
                      addToast(
                        "error",
                        "Telegram verify failed",
                        error instanceof Error ? error.message : "Could not verify Telegram auth."
                      ),
                  }
                );
              }}
            >
              Verify Telegram
            </button>
          </details>
        ) : null}
        <label className="ev-v2-field">
          <span>Selected relationship</span>
          <input value={selectedContact?.person.fullName ?? "No contact selected"} readOnly />
        </label>
        <label className="ev-v2-field" data-ev-tour="telegram-chat-url">
          <span>Telegram DM/group URL</span>
          <input
            value={telegramChatUrl}
            onChange={(event) => setTelegramChatUrl(event.target.value)}
            placeholder="https://t.me/founder or group"
          />
        </label>
        <label className="ev-v2-field" data-ev-tour="telegram-message-body">
          <span>Message</span>
          <textarea
            value={telegramBody}
            onChange={(event) => setTelegramBody(event.target.value)}
            placeholder="Hey — quick follow-up here."
          />
        </label>
        <button
          type="button"
          className="btn"
          disabled={
            telegramSendMutation.isPending || !activeItem || !effectiveSelectedRelationshipId
          }
          onClick={() => void sendTelegramFollowUp()}
        >
          {telegramSendMutation.isPending ? "Sending Telegram..." : "Send Telegram"}
        </button>
      </section>

      <section className="ev-v2-dock-section" data-ev-tour="x-dm-section">
        <div className="ev-v2-panel__head ev-v2-panel__head--compact">
          <div>
            <span className="ev-v2-mini-label">X DM Preview / Send</span>
            <p className="ev-v2-muted">
              {xStatusQuery.data?.connected
                ? xStatusQuery.data.dmSendEnabled
                  ? "X connected. Preview, edit, then send."
                  : "X connected, but live DM sending is disabled by env."
                : xStatusQuery.data?.oauthConfigured
                  ? "Connect X before sending DMs."
                  : "X OAuth not configured."}
            </p>
          </div>
        </div>
        <div className="ev-v2-actions-row" data-ev-tour="x-connect-status">
          <button
            type="button"
            className="btn btn-small btn-secondary"
            disabled={startXConnectMutation.isPending || xStatusQuery.data?.connected === true}
            onClick={() => void startXConnect()}
          >
            {xStatusQuery.data?.connected ? "X Connected" : "Connect X"}
          </button>
          {xStatusQuery.data?.connection ? (
            <button
              type="button"
              className="btn btn-small btn-secondary"
              disabled={disconnectXMutation.isPending}
              onClick={() => {
                disconnectXMutation.mutate(undefined, {
                  onSuccess: () => addToast("info", "X disconnected", "X connection removed."),
                  onError: (error) =>
                    addToast(
                      "error",
                      "X disconnect failed",
                      error instanceof Error ? error.message : "Could not disconnect X."
                    ),
                });
              }}
            >
              Disconnect X
            </button>
          ) : null}
        </div>
        <div className="ev-v2-parsed-url" data-ev-tour="selected-x-handle">
          <span className="ev-v2-mini-label">Selected X handle</span>
          <strong>
            {selectedXHandle ? `@${selectedXHandle}` : "No X handle on selected contact"}
          </strong>
          {xResolvedHandle && xResolvedHandle.handle === selectedXHandle ? (
            <span className="mono">
              Resolved: @{xResolvedHandle.handle}
              {xResolvedHandle.xUserId ? ` / ${xResolvedHandle.xUserId}` : ""}
            </span>
          ) : null}
        </div>
        <button
          type="button"
          className="btn btn-small btn-secondary"
          data-ev-tour="x-resolve-handle"
          disabled={!selectedXHandle || xResolveHandleMutation.isPending}
          onClick={() => void resolveSelectedXHandle()}
        >
          {xResolveHandleMutation.isPending ? "Resolving X..." : "Resolve selected X handle"}
        </button>
        <label className="ev-v2-field" data-ev-tour="x-dm-body">
          <span>X DM body</span>
          <textarea
            value={xBody}
            onChange={(event) => {
              setXBody(event.target.value);
              if (event.target.value.trim() !== xPreviewedDraft?.trim()) {
                setXPreviewSimilarityBlocked(false);
              }
            }}
            placeholder="Preview a generated X DM or write one manually."
          />
        </label>
        {xPreviewSimilarityBlocked && xBody.trim() === xPreviewedDraft?.trim() ? (
          <div className="ev-v2-ambiguous-box">
            Server marked this X draft as too similar. Edit it materially or repair the draft before
            sending.
          </div>
        ) : null}
        <div className="ev-v2-actions-row">
          <button
            type="button"
            className="btn btn-secondary"
            data-ev-tour="x-preview-button"
            disabled={xPreviewMutation.isPending || !activeItem || !effectiveSelectedRelationshipId}
            onClick={() => void previewXFollowUp()}
          >
            {xPreviewMutation.isPending ? "Previewing..." : "Preview X DM"}
          </button>
          <button
            type="button"
            className="btn"
            data-ev-tour="x-send-button"
            disabled={
              xSendMutation.isPending ||
              !activeItem ||
              !effectiveSelectedRelationshipId ||
              !xBody.trim() ||
              (xPreviewSimilarityBlocked && xBody.trim() === xPreviewedDraft?.trim())
            }
            onClick={() => void sendXFollowUp()}
          >
            {xSendMutation.isPending ? "Sending X..." : "Send X DM"}
          </button>
        </div>
      </section>

      <details className="ev-v2-details" open data-ev-tour="x-thread-audit-details">
        <summary>X thread / audit history</summary>
        <div className="ev-v2-mini-list">
          <span className="ev-v2-mini-label">Recent thread</span>
          {xThreadQuery.data?.length ? (
            xThreadQuery.data.slice(0, 5).map((message) => (
              <div key={message.id} className="ev-v2-mini-list__item ev-v2-mini-list__item--draft">
                <p>{message.body}</p>
                <span>
                  {message.direction} · {message.status} ·{" "}
                  {formatDateTime(message.sentAt ?? message.createdAt)}
                </span>
              </div>
            ))
          ) : (
            <span className="ev-v2-muted">
              {effectiveSelectedRelationshipId
                ? "No X thread messages yet."
                : "Select a contact to load X thread history."}
            </span>
          )}
        </div>
        <div className="ev-v2-mini-list">
          <span className="ev-v2-mini-label">Audit trail</span>
          {xAuditQuery.data?.length ? (
            xAuditQuery.data.slice(0, 5).map((entry) => (
              <div key={entry.id} className="ev-v2-mini-list__item">
                <div>
                  <strong>
                    {entry.action} · {entry.status}
                  </strong>
                  <span>
                    {entry.sendSource ?? "x"} · {formatDateTime(entry.createdAt)}
                    {entry.errorMessage ? ` · ${entry.errorMessage}` : ""}
                  </span>
                </div>
              </div>
            ))
          ) : (
            <span className="ev-v2-muted">
              {effectiveSelectedRelationshipId
                ? "No X audit entries yet."
                : "Select a contact to load X audit history."}
            </span>
          )}
        </div>
      </details>

      <details className="ev-v2-details" data-ev-tour="x-settings-queue-details">
        <summary>X settings / queue</summary>
        <div className="ev-v2-stack">
          <label className="ev-v2-field">
            <span>X prompt template</span>
            <textarea
              value={xPromptTemplate}
              onChange={(event) => setXPromptTemplate(event.target.value)}
              placeholder="Optional house style or prompt guidance for generated X DMs."
            />
          </label>
          <div className="ev-v2-actions-row">
            <button
              type="button"
              className="btn btn-small btn-secondary"
              disabled={saveXPromptSettingsMutation.isPending}
              onClick={() => void saveXPromptTemplate()}
            >
              {saveXPromptSettingsMutation.isPending ? "Saving prompt..." : "Save X prompt"}
            </button>
            <button
              type="button"
              className="btn btn-small btn-secondary"
              disabled={!activeItem || xPromptExamplesMutation.isPending}
              onClick={() => void generateXPromptExamplesForActiveProject()}
            >
              {xPromptExamplesMutation.isPending ? "Generating examples..." : "Generate examples"}
            </button>
          </div>
          {xPromptExamples.length > 0 ? (
            <div className="ev-v2-mini-list">
              <span className="ev-v2-mini-label">Prompt examples</span>
              {xPromptExamples.map((example) => (
                <div
                  key={example.projectId}
                  className="ev-v2-mini-list__item ev-v2-mini-list__item--draft"
                >
                  <p>{example.prompt}</p>
                </div>
              ))}
            </div>
          ) : null}
          <div className="ev-v2-mini-list">
            <span className="ev-v2-mini-label">Queue contacts</span>
            {xQueueContactsQuery.data?.length ? (
              xQueueContactsQuery.data.map((contact) => (
                <div key={contact.relationshipId} className="ev-v2-mini-list__item">
                  <div>
                    <strong>{contact.person.fullName}</strong>
                    <span>
                      {contact.targetLabel} · {contact.xHandle ? `@${contact.xHandle}` : "no X"}
                    </span>
                  </div>
                  <button
                    type="button"
                    className="btn btn-small btn-secondary"
                    disabled={!contact.xHandle || xQueuePreviewMutation.isPending}
                    onClick={() => void previewQueuedXContact(contact.relationshipId)}
                  >
                    Preview
                  </button>
                </div>
              ))
            ) : (
              <span className="ev-v2-muted">No X queue contacts match the current search.</span>
            )}
          </div>
          <div className="ev-v2-mini-list">
            <span className="ev-v2-mini-label">Draft queue</span>
            {xQueueDraftsQuery.data?.length ? (
              xQueueDraftsQuery.data.slice(0, 4).map((draft) => {
                const draftBlocked = draft.similarityBlocked || draft.status === "repair_needed";
                return (
                  <div
                    key={draft.id}
                    className="ev-v2-mini-list__item ev-v2-mini-list__item--draft"
                  >
                    <p>{draft.body}</p>
                    {draftBlocked ? (
                      <span className="ev-v2-muted">
                        Repair needed before send
                        {draft.repairReason ? `: ${draft.repairReason}` : "."}
                      </span>
                    ) : null}
                    <div className="ev-v2-actions-row">
                      <button
                        type="button"
                        className="btn btn-small"
                        disabled={xQueueSendMutation.isPending || draftBlocked}
                        onClick={() => void sendQueuedXDraft(draft.id, draft.body)}
                      >
                        Send
                      </button>
                      <button
                        type="button"
                        className="btn btn-small btn-secondary"
                        disabled={discardXQueueDraftMutation.isPending}
                        onClick={() => {
                          discardXQueueDraftMutation.mutate(draft.id, {
                            onSuccess: () =>
                              addToast("info", "X draft discarded", "Draft removed."),
                            onError: (error) =>
                              addToast(
                                "error",
                                "Discard failed",
                                error instanceof Error ? error.message : "Could not discard draft."
                              ),
                          });
                        }}
                      >
                        Discard
                      </button>
                      <button
                        type="button"
                        className="btn btn-small btn-secondary"
                        disabled={repairXQueueDraftMutation.isPending}
                        onClick={() => {
                          repairXQueueDraftMutation.mutate(draft.id, {
                            onSuccess: () =>
                              addToast("info", "X draft flagged", "Draft marked for repair."),
                            onError: (error) =>
                              addToast(
                                "error",
                                "Repair flag failed",
                                error instanceof Error
                                  ? error.message
                                  : "Could not flag draft for repair."
                              ),
                          });
                        }}
                      >
                        Repair
                      </button>
                    </div>
                  </div>
                );
              })
            ) : (
              <span className="ev-v2-muted">No previewed X drafts.</span>
            )}
          </div>
        </div>
      </details>

      <details className="ev-v2-details" open={false} data-ev-tour="today-brief-details">
        <summary>Today's Brief</summary>
        <div className="ev-v2-brief-slot">{todayBriefNode}</div>
      </details>

      <details className="ev-v2-details" data-ev-tour="connection-settings-details">
        <summary>Connection / settings status</summary>
        <div className="ev-v2-status-slot">{googleStatusNode}</div>
      </details>
    </aside>
  );
}

export function EvWorkstationDashboard(props: EvWorkstationDashboardProps) {
  return (
    <div className="ev-workstation-dashboard-shell">
      <WorkstationLaneTabs
        lane={props.lane}
        leadCount={props.leadCount}
        inTouchCount={props.inTouchCount}
        onLaneChange={props.onLaneChange}
      />
      <div className="ev-workstation-v2" data-ev-tour="dashboard-grid">
        <WorkstationQueueRail
          lane={props.lane}
          queue={props.queue}
          activeItem={props.activeItem}
          projectSearch={props.projectSearch}
          onProjectSearchChange={props.onProjectSearchChange}
          onSelectProject={props.onSelectProject}
        />
        <WorkstationProjectCard
          lane={props.lane}
          item={props.activeItem}
          isSaving={
            props.activeItem ? props.savingProjectIds.has(props.activeItem.project.id) : false
          }
          onNext={props.onNext}
          onOpenProjectDetail={props.onOpenProjectDetail}
          onUpdateWorkflow={props.onUpdateWorkflow}
          onUpdateIntroContact={props.onUpdateIntroContact}
          onCreateWorkflowEvent={props.onCreateWorkflowEvent}
          addToast={props.addToast}
        />
        <WorkstationActionDock
          activeItem={props.activeItem}
          contactSearch={props.contactSearch}
          onContactSearchChange={props.onContactSearchChange}
          onUpdateWorkflow={props.onUpdateWorkflow}
          onCreateWorkflowEvent={props.onCreateWorkflowEvent}
          onNext={props.onNext}
          todayBriefNode={props.todayBriefNode}
          googleStatusNode={props.googleStatusNode}
          addToast={props.addToast}
        />
      </div>
    </div>
  );
}
